import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../context/AdminContext";
import { ShieldCheck } from "lucide-react";

const LOGO = "/logo-kb-icon.png";

export default function AdminLogin() {
    const { login, login2fa } = useAdmin();
    const navigate = useNavigate();
    const [form, setForm]       = useState({ username: "", password: "" });
    const [twoFA, setTwoFA]     = useState({ visible: false, code: "", partial_token: "" });
    const [error, setError]     = useState("");
    const [loading, setLoading] = useState(false);

    const submitCredentials = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const result = await login(form.username, form.password);
            if (result?.needs_2fa) {
                setTwoFA({ visible: true, code: "", partial_token: result.partial_token });
            } else {
                navigate("/admin/referencie");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const submit2fa = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login2fa(twoFA.partial_token, twoFA.code);
            navigate("/admin/referencie");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-nova-black flex items-center justify-center px-4">
            <div className="w-full max-w-sm">
                <div className="flex items-center gap-3 justify-center mb-10">
                    <img src={LOGO} alt="KB MUSIC" className="w-10 h-10 rounded-full ring-1 ring-nova-cyan/30" />
                    <div>
                        <div className="font-heading font-black text-white text-lg tracking-tight leading-none">KB MUSIC</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin panel</div>
                    </div>
                </div>

                {!twoFA.visible ? (
                    <form onSubmit={submitCredentials} className="border border-white/8 bg-nova-surface/40 rounded-xl p-8 space-y-4">
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Meno</label>
                            <input
                                type="text"
                                value={form.username}
                                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60"
                                placeholder="admin"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Heslo</label>
                            <input
                                type="password"
                                value={form.password}
                                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                className="w-full h-10 px-3 rounded-md bg-nova-black border border-white/10 text-white text-sm focus:outline-none focus:border-nova-cyan/60"
                                placeholder="••••••••"
                            />
                        </div>
                        {error && <div className="text-red-400 text-sm font-mono">{error}</div>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-md bg-nova-cyan text-black font-semibold text-sm hover:saturate-125 transition-all disabled:opacity-60"
                        >
                            {loading ? "Prihlasovanie…" : "Prihlásiť sa"}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={submit2fa} className="border border-white/8 bg-nova-surface/40 rounded-xl p-8 space-y-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-lg bg-nova-cyan/10 border border-nova-cyan/30 flex items-center justify-center">
                                <ShieldCheck className="w-4 h-4 text-nova-cyan" />
                            </div>
                            <div>
                                <div className="text-white text-sm font-semibold">Dvojfaktorová autentifikácia</div>
                                <div className="text-zinc-500 text-xs">Zadajte kód z autentifikátora</div>
                            </div>
                        </div>
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">6-miestny kód</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9 ]*"
                                maxLength={7}
                                value={twoFA.code}
                                onChange={e => setTwoFA(s => ({ ...s, code: e.target.value }))}
                                className="w-full h-12 px-3 rounded-md bg-nova-black border border-white/10 text-white text-xl font-mono text-center tracking-[0.4em] focus:outline-none focus:border-nova-cyan/60"
                                placeholder="000000"
                                autoFocus
                            />
                        </div>
                        {error && <div className="text-red-400 text-sm font-mono">{error}</div>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-md bg-nova-cyan text-black font-semibold text-sm hover:saturate-125 transition-all disabled:opacity-60"
                        >
                            {loading ? "Overujem…" : "Potvrdiť"}
                        </button>
                        <button
                            type="button"
                            onClick={() => { setTwoFA({ visible: false, code: "", partial_token: "" }); setError(""); }}
                            className="w-full text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                        >
                            ← Späť na prihlásenie
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
